import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { CheckCircle2, Loader2, Phone, ArrowRight } from "lucide-react";

export default function EnrollSuccess() {
  const [params] = useSearchParams();
  const enrollId = params.get("enroll");
  const [state, setState] = useState({ status: "checking", plan_name: "", interval: "" });

  useEffect(() => {
    if (!enrollId) { setState({ status: "unknown" }); return; }
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/comfort-club/status/${enrollId}`);
        if (data.status === "active") { setState({ ...data }); return; }
        setState((s) => ({ ...s, ...data, status: data.status || "pending" }));
      } catch { /* keep polling */ }
      tries += 1;
      if (tries < 12) timer = setTimeout(poll, 2500);
      else setState((s) => ({ ...s, status: "pending" }));
    };
    poll();
    return () => clearTimeout(timer);
  }, [enrollId]);

  const active = state.status === "active";
  const checking = state.status === "checking";

  return (
    <>
      <Seo title="Comfort Club Enrollment | Aquila Air & Heating" description="Your Aquila Comfort Club membership enrollment confirmation." path="/service-agreement/success/" noindex />
      <section className="min-h-[70vh] flex items-center bg-slate-50 py-16" data-testid="enroll-success-page">
        <div className="max-w-xl mx-auto px-4 w-full">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
            {checking ? (
              <>
                <Loader2 className="h-10 w-10 text-launch animate-spin mx-auto" />
                <h1 className="mt-5 text-2xl font-bold text-navy">Confirming your enrollment…</h1>
                <p className="mt-2 text-slate-500 text-sm">This only takes a moment.</p>
              </>
            ) : active ? (
              <>
                <span className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 text-green-600 mx-auto"><CheckCircle2 className="h-9 w-9" /></span>
                <h1 className="mt-5 text-2xl font-bold text-navy" data-testid="enroll-success-title">You're in the Comfort Club!</h1>
                <p className="mt-2 text-slate-600">Your <strong>{state.plan_name}</strong> membership ({state.interval}) is active. A welcome email is on its way, and we'll reach out to schedule your first precision tune-up.</p>
              </>
            ) : (
              <>
                <span className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 text-amber-600 mx-auto"><Phone className="h-8 w-8" /></span>
                <h1 className="mt-5 text-2xl font-bold text-navy">Almost there</h1>
                <p className="mt-2 text-slate-600">We haven't confirmed your payment yet. If you just completed checkout, it may take a minute. If you closed the payment window, you can try again or call us.</p>
                <a href="tel:3212921189" className="mt-4 inline-flex items-center gap-2 text-launch font-semibold"><Phone className="h-4 w-4" /> 321-292-1189</a>
              </>
            )}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" data-testid="enroll-home-link" className="inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold px-6 py-2.5 rounded-lg">Back to Home <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/service-agreement" className="inline-flex items-center justify-center gap-2 border border-slate-300 text-navy font-semibold px-6 py-2.5 rounded-lg">View Plans</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
